// import { Add, Remove } from "@mui/icons-material";
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Select, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

interface Props {
  editor: any;
}

export const FontSize: React.FC<Props> = (props) => {
  const { editor } = props;

  const command = editor ? editor.commands.get('fontSize') : null;
  const [isEnabled, setIsEnabled] = useState(true);
  const [fontSize, setFontSize] = useState(12);

  const getActiveFontSize = (editor: any) => {
    const selection = editor.model.document.selection;
    const attributes = selection.getAttributes();
    const attributesMap = new Map(attributes);
    const activeFontSize = attributesMap.get('fontSize') ?? '12px';
    const fontSize = parseInt((activeFontSize as string).split('px')[0]);
    return fontSize;
  };

  useEffect(() => {
    if (editor) {
      const updateFontSize = () => {
        const activeFontSize = getActiveFontSize(editor);
        if (fontSize !== activeFontSize && activeFontSize)
          setFontSize(activeFontSize);
      };
      editor.model.document.on('change', updateFontSize);
      return () => {
        editor.model.document.off('change', updateFontSize);
      };
    }
  }, [editor, fontSize]);

  useEffect(() => {
    if (!command) return;

    function handleIsEnabledChange() {
      setIsEnabled(command.isEnabled);
    }

    command.on('change:isEnabled', handleIsEnabledChange);
    handleIsEnabledChange();

    return function cleanup() {
      command.off('change:isEnabled', handleIsEnabledChange);
    };
  }, [command]);

  const applyFontSize = ({ mode }: { mode: 'Add' | 'Reduce' }) => {
    editor.model.change((writer: any) => {
      const selection = editor.model.document.selection;

      if (selection.isCollapsed) {
        const newSize = mode === 'Add' ? fontSize + 2 : fontSize - 2;
        setFontSize(newSize);
        editor.execute('fontSize', { value: `${newSize}px` });
        return;
      }

      const range = selection.getFirstRange();
      if (!range) return;

      for (const item of range.getItems()) {
        if (item.textNode) {
          const attributes = item.textNode.getAttributes();
          const attributesMap = new Map(attributes);
          const activeFontSize = attributesMap.get('fontSize') ?? '14px';
          const fontSize = parseInt((activeFontSize as string).split('px')[0]);
          const newFontSize =
            mode === 'Add'
              ? Math.min(fontSize + 2, 72)
              : Math.max(fontSize - 2, 6);

          writer.setAttributes({ fontSize: `${newFontSize}px` }, item);
        }
      }
    });
  };

  const handleDecreaseFontSize = () => {
    applyFontSize({ mode: 'Reduce' });
  };

  const handleIncreaseFontSize = () => {
    applyFontSize({ mode: 'Add' });
  };

  const handleFontSizeChange = (value: any) => {
    setFontSize(value);
    editor.model.change((writer: any) => {
      const selection = editor.model.document.selection;

      if (selection.isCollapsed) {
        return;
      }

      const range = selection.getFirstRange();
      if (!range) return;

      for (const item of range.getItems()) {
        const newFontSize = value;
        writer.setAttributes({ fontSize: `${newFontSize}px` }, item);
      }
    });
  };

  const fontSizes = Array.from(
    { length: (72 - 6) / 2 + 1 }, // Calculate the length of the array
    (_, i) => ({
      // Generate values starting from 6 and incrementing by 2
      label: 6 + i * 2,
      value: 6 + i * 2,
    }),
  );

  return (
    <div className="tw-flex tw-gap-[2px] tw-h-full tw-items-center">
      <Tooltip title="Decrease Font">
        <Button
          type="text"
          disabled={fontSize <= 6 || !isEnabled}
          icon={<MinusOutlined />}
          onClick={handleDecreaseFontSize}
        />
      </Tooltip>

      <Select
        disabled={!isEnabled}
        options={fontSizes}
        value={fontSize}
        style={{ width: 55 }}
        popupMatchSelectWidth={false}
        onSelect={handleFontSizeChange}
      />

      <Tooltip title="Increase Font">
        <Button
          type="text"
          disabled={fontSize >= 72 || !isEnabled}
          icon={<PlusOutlined />}
          onClick={handleIncreaseFontSize}
        />
      </Tooltip>
    </div>
  );
};
